import styled from '@emotion/styled'
import { Accordion, Dialog, DialogActions, DialogTitle, IconButton, Typography } from '@mui/material'
import theme from 'theme'
import { CircleButton } from '../../../../components/buttons'

export const CustomizedDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 634px;
    padding: ${theme.spacing.md}px;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background-color: ${theme.colors.surfaceContainerBackground};

    html[data-theme='light'] & {
      box-shadow: none;
    }

    input,
    .MuiSelect-select {
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.bold};
      color: ${theme.colors.menuItemText};
      padding: 12.5px 14px;
    }

    textarea {
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.bold};
      color: ${theme.colors.menuItemText};
    }

    .MuiInputBase-input::placeholder {
      color: ${theme.colors.devicesSecondaryText};
      opacity: 1;
    }

    .MuiInputLabel-root {
      transform: translate(14px, -9px) scale(0.75);
      color: ${theme.colors.dropdownLabel};
      background-color: ${theme.colors.labelCardBg};
      padding: 0 4px;

      &.Mui-focused {
        padding: 0 4px;
        background-color: ${theme.colors.labelCardBg};
      }

      html[data-theme='light'] & {
        color: ${theme.colors.menuItemText};
        background-color: #fafafb;
      }
    }

    /* The v2 fields force the dark-mode error pink with !important. */
    html[data-theme='light'] & .MuiFormHelperText-root.Mui-error {
      color: ${theme.colors.inputError} !important;
    }

    .MuiOutlinedInput-notchedOutline legend {
      width: 0;
    }
  }
`

export const StyledDialogTitle = styled(DialogTitle)`
  font-size: ${theme.typography.fontSize.xxxxLarge};
  color: ${theme.colors.menuItemText};
`

export const CloseButton = styled(IconButton)`
  color: ${theme.colors.devicesSecondaryText};

  &:hover {
    background-color: transparent;
    color: ${theme.colors.cta};
  }
`

export const StyledAccordion = styled(Accordion)`
  padding: ${theme.spacing.xSmall}px;
  margin-bottom: 0 !important;
  background-color: ${theme.colors.labelCardBg};
  border-radius: ${theme.attributes.borderRadius.tertiary} !important;

  html[data-theme='light'] & {
    box-shadow: none;
    background-color: #fafafb;
    border: 1px solid ${theme.colors.strokeSecondary};

    &:not(.Mui-expanded) .MuiAccordionSummary-content .MuiTypography-root {
      color: ${theme.colors.textCaption};
    }

    /* MUI's divider is full-width and unrounded, so it cuts across the corners. */
    &::before {
      display: none;
    }
  }

  .MuiAccordionSummary-root {
    min-height: unset;
    padding: 0 ${theme.spacing.xSmall}px;

    &.Mui-expanded {
      min-height: unset;
    }
  }

  .MuiAccordionSummary-content {
    margin: ${theme.spacing.xSmall}px 0;

    &.Mui-expanded {
      margin: ${theme.spacing.xSmall}px 0;
    }
  }

  .MuiAccordionSummary-expandIconWrapper {
    svg {
      color: ${theme.colors.devicesSecondaryText};
    }
  }

  &.Mui-disabled,
  &.fake-disabled {
    background-color: ${theme.colors.labelCardBg};

    .MuiAccordionSummary-root {
      opacity: 0.8;
    }

    .MuiAccordionSummary-expandIconWrapper {
      svg {
        color: ${theme.colors.dropdownLabel};
      }
    }

    .MuiTypography-root {
      color: ${theme.colors.dropdownLabel};
    }
  }

  &.Mui-disabled {
    .MuiButton-root {
      background-color: transparent;
      border: 1px solid ${theme.colors.tooltipBg};
      color: ${theme.colors.menuItemText};
    }
  }

  &.fake-disabled {
    .MuiTypography-root {
      font-weight: ${theme.typography.fontWeight.extraThick};
    }

    .MuiButton-root {
      background-color: ${theme.colors.tooltipBg};
      border: 1px solid ${theme.colors.btnTertiaryBg};
      color: ${theme.colors.dropdownLabel};
    }
  }
`

export const StepButton = styled(CircleButton)`
  background-color: ${theme.colors.cta};
  color: ${theme.colors.pageBackground};
  font-size: ${theme.typography.fontSize.xSmall}px;

  &:hover {
    background-color: ${theme.colors.cta};
    color: ${theme.colors.pageBackground};
  }

  svg {
    font-size: ${theme.iconSize.small}px;
  }
`

export const StepTitle = styled(Typography)`
  font-size: ${theme.typography.fontSize.header};
  color: ${theme.colors.menuItemText};
  font-weight: ${theme.typography.fontWeight.bold};
`

export const SwitchLabel = styled(Typography)`
  font-size: ${theme.typography.fontSize.large};
  color: ${theme.colors.menuItemText};
`

export const ControlLabel = styled(Typography)`
  color: ${theme.colors.dropdownLabel};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.normal};
`

export const ControlValue = styled(Typography)`
  margin-top: ${theme.spacing.xSmall}px !important;
  color: ${theme.colors.menuItemText};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
`

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1500;
  /* Track the paper's own radius so the corners can't show an undimmed sliver. */
  border-radius: inherit;
`

export const StyledDialogActions = styled(DialogActions)`
  display: block;
  padding-left: 22px;
  padding-right: 22px;
`
